/**
 * Copyright Warner Bros. Entertainment, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Warner Bros. Entertainment, Inc. and its suppliers, if any.
 * The intellectual and technical concepts contained herein are
 * proprietary to Warner Bros. Entertainment, Inc. and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in process,
 * and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * unlawful and strictly forbidden unless prior written permission is
 * obtained from Warner Bros. Entertainment, Inc.
 */

const languageCodeType = {
    DEFAULT_LANGUAGE: 'EN',
    LANGUAGES: {
        AR: 'Arabic',
        BG: 'Bulgarian',
        CMN: 'Mandarin',
        CS: 'Czech',
        DA: 'Danish',
        EL: 'Greek',
        EN: 'English', // no Localization
        ET: 'Estonian',
        FA: 'Persian',
        FI: 'Finnish',
        FR: 'French',
        FR_CA: 'French (Canadian)',
        GER: 'German',
        HE: 'Hebrew',
        HIF: 'Hindi',
        HR: 'Croatian',
        HU: 'Hungarian',
        ID: 'Bahasa Indonesia',
        IS: 'Icelandic',
        ITA: 'Italian',
        JPN: 'Japanese',
        KO: 'Korean',
        LT: 'Lithuanian',
        LV: 'Latvian',
        MK: 'Macedonian',
        MS: 'Bahasa Malay',
        NL: 'Dutch',
        NO: 'Norwegian',
        PA: 'Panjabi',
        PL: 'Polish',
        POR: 'Portuguese (European)',
        POR_BR: 'Portuguese (Brazilian)',
        PRS: 'Dari',
        RO: 'Romanian',
        RU: 'Russian',
        SK: 'Slovak',
        SPA: 'Spanish (Castilian)',
        SPA_LAT: 'Spanish (Latin Spanish)',
        SR: 'Serbian',
        SV: 'Swedish',
        TA: 'Tamil',
        TH: 'Thai',
        TL: 'Tagalog',
        TR: 'Turkish',
        UR: 'Urdu',
        VI: 'Vietnamese',
        ZH_HANS: 'Chinese (Simplified)',
        ZH_HANT: 'Chinese (Traditional)',
        ZH_HK: 'Chinese (Hong Kong)',
        ZH_TW: 'Chinese (Taiwan)',
        ZLM: 'Malay',
    },
    LANGUAGES_BY_NAME: {
        ARABIC: 'AR',
        BAHASA_INDONESIA: 'ID',
        BAHASA_MALAY: 'MS',
        BULGARIAN: 'BG',
        CHINESE_HONG_KONG: 'ZH_HK',
        CHINESE_SIMPLIFIED: 'ZH_HANS',
        CHINESE_TAIWAN: 'ZH_TW',
        CHINESE_TRADITIONAL: 'ZH_HANT',
        CROATIAN: 'HR',
        CZECH: 'CS',
        DANISH: 'DA',
        DARI: 'PRS',
        DUTCH: 'NL',
        ENGLISH: 'EN',
        ESTONIAN: 'ET',
        FINNISH: 'FI',
        FRENCH: 'FR',
        FRENCH_CANADIAN: 'FR_CA',
        GERMAN: 'GER',
        GREEK: 'EL',
        HEBREW: 'HE',
        HINDI: 'HIF',
        HUNGARIAN: 'HU',
        ICELANDIC: 'IS',
        ITALIAN: 'ITA',
        JAPANESE: 'JPN',
        KOREAN: 'KO',
        LATVIAN: 'LV',
        LITHUANIAN: 'LT',
        MACEDONIAN: 'MK',
        MALAY: 'ZLM',
        MANDARIN: 'CMN',
        NORWEGIAN: 'NO',
        PANJABI: 'PA',
        PERSIAN: 'FA',
        POLISH: 'PL',
        PORTUGUESE: 'POR',
        PORTUGUESE_BRA: 'POR_BR',
        ROMANIAN: 'RO',
        RUSSIAN: 'RU',
        SERBIAN: 'SR',
        SLOVAK: 'SK',
        SPANISH: 'SPA',
        SPANISH_LATAM: 'SPA_LAT',
        SWEDISH: 'SV',
        TAGALOG: 'TL',
        TAMIL: 'TA',
        THAI: 'TH',
        TURKISH: 'TR',
        URDU: 'UR',
        VIETNAMESE: 'VI'
    },
    toArray: function() {
        return Object.keys(this.LANGUAGES).map(k => ({id: k, name: this.LANGUAGES[k]})).sort((a, b) => a.name.localeCompare(b.name));
    }
};

const GetSubtitleLanguageByCode = (langCode) => {
    // STUDIO-11154 refactor of wbtvd/player/bitmovin-ui/label-utils
    const getNameByCode = (c) => {
        return languageCodeType.LANGUAGES[c];
    };
    switch (langCode) {
    case 'ar': case 'ar-bh': case 'ara': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.ARABIC);
    case 'bg': case 'bg-bg': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.BULGARIAN);
    case 'cmn': case 'zh-cn': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.MANDARIN);
    case 'cs': case 'cs-cz': case 'ces': case 'cze': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.CZECH);
    case 'da': case 'da-dk': case 'dan': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.DANISH);
    case 'de': case 'de-de': case 'ger': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.GERMAN);
    case 'el': case 'el-gr': case 'ell': case 'gre': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.GREEK);
    case 'en': case 'en-us': case 'eng': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.ENGLISH);
    case 'es': case 'es-es': case 'spa': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.SPANISH);
    case 'es-419': case 'es_419': case 'es-la': case 'spa_lat': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.SPANISH_LATAM);
    case 'et': case 'et-ee': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.ESTONIAN);
    case 'fa': case 'fa-ir': case 'fas': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.PERSIAN);
    case 'fi': case 'fi-fi': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.FINNISH);
    case 'fr': case 'fr-fr': case 'fra': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.FRENCH);
    case 'fr-ca': case 'fr_ca': case 'fre': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.FRENCH_CANADIAN);
    case 'he': case 'he-il': case 'heb': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.HEBREW);
    case 'hi': case 'hi-in': case 'hif': case 'hin': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.HINDI);
    case 'hr': case 'hr-hr': case 'hrv': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.CROATIAN);
    case 'hu': case 'hu-uh': case 'hun': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.HUNGARIAN);
    case 'id': case 'id-id': case 'ind': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.BAHASA_INDONESIA);
    case 'is': case 'is-is': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.ICELANDIC);
    case 'it': case 'it-it': case 'ita': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.ITALIAN);
    case 'ja': case 'ja-jp': case 'jpn': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.JAPANESE);
    case 'ko': case 'ko-kr': case 'kor': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.KOREAN);
    case 'lt': case 'lt-lt': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.LITHUANIAN);
    case 'lv': case 'lv-lv': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.LATVIAN);
    case 'mk': case 'mk-mk': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.MACEDONIAN);
    case 'nl': case 'nl-nl': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.DUTCH);
    case 'no': case 'nb-no': case 'nor': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.NORWEGIAN);
    case 'pa': case 'pan': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.PANJABI);
    case 'pl': case 'pl-pl': case 'pol': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.POLISH);
    case 'prs': case 'fa-af': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.DARI);
    case 'pt': case 'pt-pt': case 'por': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.PORTUGUESE);
    case 'pt-br': case 'pt_br': case 'por_br': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.PORTUGUESE_BRA);
    case 'ro': case 'ro-ro': case 'ron': case 'rm': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.ROMANIAN);
    case 'ru': case 'ru-ru': case 'rus': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.RUSSIAN);
    case 'sk': case 'sk_sk': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.SLOVAK);
    case 'sr': case 'sr-sp': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.SERBIAN);
    case 'sv': case 'sv-se': case 'swe': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.SWEDISH);
    case 'ta': case 'ta-in': case 'tam': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.TAMIL);
    case 'th': case 'th-th': case 'tha': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.THAI);
    case 'tl': case 'tlg': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.TAGALOG);
    case 'tr': case 'tr-tr': case 'tur': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.TURKISH);
    case 'ur': case 'ur-pk': case 'urd': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.URDU);
    case 'vi': case 'vi-vn': case 'vie': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.VIETNAMESE);
    case 'zh': case 'zh-hans': case 'zh_hans': case 'chs': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.CHINESE_SIMPLIFIED);
    case 'zh-hant': case 'zh_hant': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.CHINESE_TRADITIONAL);
    case 'zh_hk': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.CHINESE_HONG_KONG);
    case 'zh_tw': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.CHINESE_TAIWAN);
    case 'zlm': case 'ms': case 'ms-my': case 'mas': return getNameByCode(languageCodeType.LANGUAGES_BY_NAME.MALAY);
    default:
        // TODO: determine if further modification is needed.
        // return 'English';
        return langCode;
    }
};

export {
    languageCodeType as LanguageCodeType,
    GetSubtitleLanguageByCode
};
